/**
 * VenueOS Message Protocol
 *
 * Defines the message types and structures used for postMessage
 * communication between the VenueOS shell and embedded applications.
 */
/**
 * Message type constants for the VenueOS protocol
 */
export const VENUEOS_MESSAGE_TYPES = {
    /** Sent by app to request context from shell */
    HANDSHAKE: 'VENUEOS_HANDSHAKE',
    /** Sent by shell with initial context */
    CONTEXT: 'VENUEOS_CONTEXT',
    /** Sent by shell when context changes */
    CONTEXT_UPDATED: 'VENUEOS_CONTEXT_UPDATED',
    /** Sent by app to request map data (MVF URL + tiles config) */
    MAP_DATA_REQUEST: 'VENUEOS_MAP_DATA_REQUEST',
    /** Sent by shell with map data response */
    MAP_DATA_RESPONSE: 'VENUEOS_MAP_DATA_RESPONSE',
    /** Sent by app to request an API call (proxied through shell) */
    API_REQUEST: 'VENUEOS_API_REQUEST',
    /** Sent by shell with API response */
    API_RESPONSE: 'VENUEOS_API_RESPONSE',
    /** Sent by app to request shell UI changes (e.g., hide venue selector) */
    SHELL_UI_REQUEST: 'VENUEOS_SHELL_UI_REQUEST',
    /** Sent by app to request media URL resolution */
    MEDIA_URL_REQUEST: 'VENUEOS_MEDIA_URL_REQUEST',
    /** Sent by shell with resolved media URLs */
    MEDIA_URL_RESPONSE: 'VENUEOS_MEDIA_URL_RESPONSE',
    /** Sent by app to request tiles config (no map selection required) */
    TILES_CONFIG_REQUEST: 'VENUEOS_TILES_CONFIG_REQUEST',
    /** Sent by shell with tiles config */
    TILES_CONFIG_RESPONSE: 'VENUEOS_TILES_CONFIG_RESPONSE',
    /** Sent by app to notify shell of internal route change (for URL deep linking) */
    ROUTE_CHANGED: 'VENUEOS_ROUTE_CHANGED',
};
/**
 * Current protocol version
 */
export const VENUEOS_PROTOCOL_VERSION = '1.0.0';
/**
 * Type guard to check if a message is a valid VenueOS message
 */
export function isVenueOSMessage(data) {
    if (typeof data !== 'object' || data === null) {
        return false;
    }
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- narrowed by typeof check above
    const message = data;
    return (typeof message.type === 'string' &&
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- guarded by typeof string check
        Object.values(VENUEOS_MESSAGE_TYPES).includes(message.type) &&
        typeof message.version === 'string');
}
/**
 * Type guard for context messages
 */
export function isContextMessage(message) {
    return (message.type === VENUEOS_MESSAGE_TYPES.CONTEXT ||
        message.type === VENUEOS_MESSAGE_TYPES.CONTEXT_UPDATED);
}
/**
 * Type guard for handshake messages
 */
export function isHandshakeMessage(message) {
    return message.type === VENUEOS_MESSAGE_TYPES.HANDSHAKE;
}
/**
 * Type guard for map data request messages
 */
export function isMapDataRequestMessage(message) {
    return message.type === VENUEOS_MESSAGE_TYPES.MAP_DATA_REQUEST;
}
/**
 * Type guard for map data response messages
 */
export function isMapDataResponseMessage(message) {
    return message.type === VENUEOS_MESSAGE_TYPES.MAP_DATA_RESPONSE;
}
/**
 * Type guard for API request messages
 */
export function isApiRequestMessage(message) {
    return message.type === VENUEOS_MESSAGE_TYPES.API_REQUEST;
}
/**
 * Type guard for API response messages
 */
export function isApiResponseMessage(message) {
    return message.type === VENUEOS_MESSAGE_TYPES.API_RESPONSE;
}
/**
 * Create a handshake message
 * @param token - Security token from URL parameter (required)
 */
export function createHandshakeMessage(token) {
    return {
        type: VENUEOS_MESSAGE_TYPES.HANDSHAKE,
        version: VENUEOS_PROTOCOL_VERSION,
        token,
    };
}
/**
 * Create a context message
 */
export function createContextMessage(context, isUpdate = false) {
    return {
        type: isUpdate ? VENUEOS_MESSAGE_TYPES.CONTEXT_UPDATED : VENUEOS_MESSAGE_TYPES.CONTEXT,
        version: VENUEOS_PROTOCOL_VERSION,
        payload: context,
    };
}
/**
 * Create a map data request message
 */
export function createMapDataRequestMessage(requestId, mapId) {
    return {
        type: VENUEOS_MESSAGE_TYPES.MAP_DATA_REQUEST,
        version: VENUEOS_PROTOCOL_VERSION,
        requestId,
        ...(mapId && { mapId }),
    };
}
/**
 * Create a map data response message
 */
export function createMapDataResponseMessage(payload) {
    return {
        type: VENUEOS_MESSAGE_TYPES.MAP_DATA_RESPONSE,
        version: VENUEOS_PROTOCOL_VERSION,
        payload,
    };
}
/**
 * Create an API request message
 */
export function createApiRequestMessage(requestId, path, method = 'GET', body, headers) {
    return {
        type: VENUEOS_MESSAGE_TYPES.API_REQUEST,
        version: VENUEOS_PROTOCOL_VERSION,
        requestId,
        path,
        method,
        body,
        headers,
    };
}
/**
 * Create an API response message
 */
export function createApiResponseMessage(payload) {
    return {
        type: VENUEOS_MESSAGE_TYPES.API_RESPONSE,
        version: VENUEOS_PROTOCOL_VERSION,
        payload,
    };
}
/**
 * Type guard for shell UI request messages
 */
export function isShellUIRequestMessage(message) {
    return message.type === VENUEOS_MESSAGE_TYPES.SHELL_UI_REQUEST;
}
/**
 * Create a shell UI request message
 */
export function createShellUIRequestMessage(options) {
    return {
        type: VENUEOS_MESSAGE_TYPES.SHELL_UI_REQUEST,
        version: VENUEOS_PROTOCOL_VERSION,
        payload: options,
    };
}
/**
 * Type guard for media URL request messages
 */
export function isMediaUrlRequestMessage(message) {
    return message.type === VENUEOS_MESSAGE_TYPES.MEDIA_URL_REQUEST;
}
/**
 * Type guard for media URL response messages
 */
export function isMediaUrlResponseMessage(message) {
    return message.type === VENUEOS_MESSAGE_TYPES.MEDIA_URL_RESPONSE;
}
/**
 * Create a media URL request message
 */
export function createMediaUrlRequestMessage(requestId, assetIds, fullMetadata = false) {
    return {
        type: VENUEOS_MESSAGE_TYPES.MEDIA_URL_REQUEST,
        version: VENUEOS_PROTOCOL_VERSION,
        requestId,
        assetIds,
        fullMetadata,
    };
}
/**
 * Create a media URL response message
 */
export function createMediaUrlResponseMessage(payload) {
    return {
        type: VENUEOS_MESSAGE_TYPES.MEDIA_URL_RESPONSE,
        version: VENUEOS_PROTOCOL_VERSION,
        payload,
    };
}
/**
 * Type guard for tiles config request messages
 */
export function isTilesConfigRequestMessage(message) {
    return message.type === VENUEOS_MESSAGE_TYPES.TILES_CONFIG_REQUEST;
}
/**
 * Type guard for tiles config response messages
 */
export function isTilesConfigResponseMessage(message) {
    return message.type === VENUEOS_MESSAGE_TYPES.TILES_CONFIG_RESPONSE;
}
/**
 * Create a tiles config request message
 */
export function createTilesConfigRequestMessage(requestId) {
    return {
        type: VENUEOS_MESSAGE_TYPES.TILES_CONFIG_REQUEST,
        version: VENUEOS_PROTOCOL_VERSION,
        requestId,
    };
}
/**
 * Create a tiles config response message
 */
export function createTilesConfigResponseMessage(payload) {
    return {
        type: VENUEOS_MESSAGE_TYPES.TILES_CONFIG_RESPONSE,
        version: VENUEOS_PROTOCOL_VERSION,
        payload,
    };
}
/**
 * Type guard for route changed messages
 */
export function isRouteChangedMessage(message) {
    return message.type === VENUEOS_MESSAGE_TYPES.ROUTE_CHANGED;
}
/**
 * Create a route changed message
 * @param route - The applet's current route (suffix to append to its base URL)
 */
export function createRouteChangedMessage(route) {
    return {
        type: VENUEOS_MESSAGE_TYPES.ROUTE_CHANGED,
        version: VENUEOS_PROTOCOL_VERSION,
        payload: { route },
    };
}
//# sourceMappingURL=messages.js.map